import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  Users,
  Wallet,
  Bell,
  User,
  LogOut,
  Settings,
  TrendingUp,
  BarChart3,
  CheckCircle2,
  X,
  Clock,
  AlertTriangle,
  Check,
  Table2,
  ArrowLeftRight,
  Receipt,
  Menu,
  Landmark,
  Target,
  Home,
  Shield,
  HelpCircle,
  DollarSign,
  ExternalLink,
  Building2,
  Briefcase
} from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateDDMMYY, formatRelativeTime } from '../utils/dateFormat';

// Admin email - only this user can see admin link
const ADMIN_EMAILS = ["vishal.sharma1977@gmail.com"];

const Navbar = () => {
  const { user, logout, API } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [groupNotificationsCount, setGroupNotificationsCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [counterOfferAmount, setCounterOfferAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchGroupNotificationsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/groups/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupNotificationsCount(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail - not critical
      console.error('Error fetching group notifications count:', error);
    }
  }, []);

  // Fetch group notifications count on mount and periodically
  useEffect(() => {
    fetchGroupNotificationsCount();
    const interval = setInterval(fetchGroupNotificationsCount, 30000);
    return () => clearInterval(interval);
  }, [fetchGroupNotificationsCount]);

  const checkRecurringAndFetchNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // First, check for new recurring transaction notifications
      await axios.post(`${API}/notifications/check-recurring`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then fetch all notifications
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  // Fetch notifications when popover opens
  useEffect(() => {
    if (notificationsOpen) {
      checkRecurringAndFetchNotifications();
    }
  }, [notificationsOpen, checkRecurringAndFetchNotifications]);

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(notif => 
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      ));
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(notif => !notif.is_read);
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        unreadNotifications.map(notif => 
          axios.put(`${API}/notifications/${notif.id}/read`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      setNotifications(notifications.map(notif => ({ ...notif, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Open notification detail dialog
  const openNotificationDetail = async (notification) => {
    setSelectedNotification(notification);
    setCounterOfferAmount('');
    setNotificationsOpen(false); // Close the dropdown
    setShowDetailDialog(true);
    
    // Mark as read when opened
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
  };

  // Handle split request actions
  const handleSplitAction = async (action) => {
    if (!selectedNotification?.data?.split_id) return;
    
    const splitId = selectedNotification.data.split_id;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (action === 'accept' || action === 'reject') {
        await axios.put(`${API}/expense-splits/${splitId}/respond?response=${action}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Split ${action}ed successfully`);
      } else if (action === 'counter') {
        const amount = parseFloat(counterOfferAmount);
        const originalAmount = selectedNotification.data.split_amount || selectedNotification.data.amount;
        
        if (isNaN(amount) || amount <= 0) {
          toast.error('Please enter a valid amount');
          setActionLoading(false);
          return;
        }
        
        if (amount > originalAmount) {
          toast.error(`Counter-offer cannot exceed original amount of ${selectedNotification.data.currency || 'USD'} ${originalAmount}`);
          setActionLoading(false);
          return;
        }
        
        await axios.put(`${API}/expense-splits/${splitId}/counter-offer`, {
          proposed_amount: amount
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Counter-offer of ${selectedNotification.data.currency || 'USD'} ${amount.toFixed(2)} submitted!`);
      }
      
      // Refresh notifications and close dialog
      await fetchNotifications();
      setShowDetailDialog(false);
      setSelectedNotification(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || `Failed to ${action} split`;
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle offline settlement accept/reject
  const handleOfflineSettlement = async (settlementId, action, notificationId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const endpoint = action === 'accept' 
        ? `${API}/settlements/accept-offline` 
        : `${API}/settlements/reject-offline`;
      
      const payload = { settlement_id: settlementId };
      if (action === 'reject') {
        payload.reason = 'Unable to verify payment';
      }
      
      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Offline payment ${action}ed successfully`);
      
      // Mark notification as read
      await markAsRead(notificationId);
      
      // Close dialog and refresh
      setShowDetailDialog(false);
      setSelectedNotification(null);
      setNotificationsOpen(false);
      await fetchNotifications();
    } catch (error) {
      console.error(`Error ${action}ing offline settlement:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} payment`);
    } finally {
      setActionLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'expense_split_request': return Users;
      case 'expense_split_accepted': return CheckCircle2;
      case 'expense_split_rejected': return X;
      case 'budget_exceeded': return AlertTriangle;
      case 'group_member_added': return Users;
      case 'recurring_expense_due': return Receipt;
      case 'recurring_income_due': return TrendingUp;
      case 'recurring_investment_due': return Landmark;
      case 'settlement_initiated': return DollarSign;
      case 'settlement_completed': return CheckCircle2;
      default: return Bell;
    }
  };

  const getIconColor = (type) => {
    const colors = {
      'expense_split_request': 'text-blue-600',
      'expense_split_accepted': 'text-green-600',
      'expense_split_rejected': 'text-red-600',
      'budget_exceeded': 'text-orange-600',
      'group_member_added': 'text-emerald-600',
      'recurring_expense_due': 'text-red-500',
      'recurring_income_due': 'text-green-500',
      'recurring_investment_due': 'text-blue-500',
      'settlement_initiated': 'text-purple-600',
      'settlement_completed': 'text-green-600',
    };
    return colors[type] || 'text-gray-600';
  };

  // Handle clicking on recurring notifications - navigate to appropriate tab
  const handleRecurringNotificationClick = (notification) => {
    markAsRead(notification.id);
    setNotificationsOpen(false);
    
    switch (notification.type) {
      case 'recurring_expense_due':
        navigate('/expense-list', { state: { scrollToApprovals: true } });
        break;
      case 'recurring_income_due':
        navigate('/income', { state: { scrollToApprovals: true } });
        break;
      case 'recurring_investment_due':
        navigate('/investments', { state: { scrollToApprovals: true } });
        break;
      default:
        break;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Mobile hamburger menu items (Income, Investments, Budget, Settlements)
  const mobileMenuItems = [
    { name: 'Income', href: '/income', icon: TrendingUp },
    { name: 'Investments', href: '/investments', icon: Landmark },
    { name: 'Budget', href: '/budgeting', icon: Target },
    { name: 'Business', href: '/business', icon: Briefcase },
    { name: 'Settlements', href: '/settlements', icon: ArrowLeftRight },
  ];

  // Navigation items - Tile views removed, using Spreadsheet views only
  const navigation = [
    { name: 'Quick Links', href: '/quick-links', icon: Home },
    { name: 'Dashboard', href: '/stats', icon: BarChart3 },
    { name: 'Groups', href: '/groups', icon: Users, badge: groupNotificationsCount },
    { name: 'Expenses', href: '/expense-list', icon: Table2 },
    { name: 'Income', href: '/income', icon: TrendingUp },
    { name: 'Cash Flow', href: '/cashflow', icon: ArrowLeftRight },
    { name: 'Investments', href: '/investments', icon: TrendingUp },
    { name: 'Budgeting', href: '/budgeting', icon: Wallet },
    { name: 'Business', href: '/business', icon: Briefcase },
    { name: 'Split Expense', href: '/split-expense', icon: Users },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-gray-200 fixed w-full top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between h-14 sm:h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-2 rounded-lg">
                <Receipt className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text">Monyply</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                    active
                      ? 'bg-emerald-100 text-emerald-700 shadow-sm'
                      : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="relative">
                    <Icon className="w-4 h-4" />
                    {item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="hidden lg:inline">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* User menu */}
          <div className="flex items-center space-x-4">
            {/* Notification Popover (visible on all screens) */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-2 -right-2 h-5 w-5 text-xs p-0 flex items-center justify-center"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0" align="end">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">Notifications</h3>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                          Mark all read
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setNotificationsOpen(false)}
                        className="h-8 w-8 p-0 hover:bg-gray-100"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                  </p>
                </div>
                
                <ScrollArea className="h-[400px]">
                  {loadingNotifications ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-sm text-gray-500">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {notifications.slice(0, 10).map((notification) => {
                        const Icon = getNotificationIcon(notification.type);
                        const iconColor = getIconColor(notification.type);
                        const isActionTaken = notification.action_taken || notification.data?.action_taken;
                        
                        return (
                          <div 
                            key={notification.id}
                            className={`p-4 hover:bg-gray-50 cursor-pointer ${
                              isActionTaken 
                                ? 'bg-gray-100 opacity-75' 
                                : !notification.is_read 
                                  ? 'bg-blue-50/50' 
                                  : ''
                            }`}
                            onClick={() => {
                              if (['recurring_expense_due', 'recurring_income_due', 'recurring_investment_due'].includes(notification.type)) {
                                handleRecurringNotificationClick(notification);
                              } else {
                                // Open notification detail dialog
                                openNotificationDetail(notification);
                              }
                            }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`p-2 rounded-full ${isActionTaken ? 'bg-gray-200 border-gray-300' : 'bg-white'} border ${!notification.is_read && !isActionTaken ? 'border-blue-200' : 'border-gray-200'}`}>
                                <Icon className={`w-4 h-4 ${isActionTaken ? 'text-gray-400' : iconColor}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <p className={`text-sm font-medium truncate ${isActionTaken ? 'text-gray-500' : !notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {notification.title}
                                  </p>
                                  {!notification.is_read && !isActionTaken && (
                                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                                  )}
                                  {isActionTaken && (
                                    <span className="text-xs text-gray-400 flex-shrink-0">Done</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                                  {notification.message}
                                </p>
                                
                                {/* Recurring Transaction Quick Actions */}
                                {['recurring_expense_due', 'recurring_income_due', 'recurring_investment_due'].includes(notification.type) && 
                                 !notification.action_taken && (
                                  <div className="flex space-x-2 mt-2">
                                    <Button
                                      size="sm"
                                      className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRecurringNotificationClick(notification);
                                      }}
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      Review & Approve
                                    </Button>
                                  </div>
                                )}
                                
                                {/* Split Request Quick Actions */}
                                {notification.type === 'expense_split_request' && 
                                 notification.data?.split_id && 
                                 !notification.data?.is_reminder && (
                                  notification.action_taken ? (
                                    <div className="flex items-center gap-1.5 mt-2 p-2 bg-gray-100 rounded text-xs">
                                      {(notification.data?.action_type === 'accept' || notification.action_type === 'accept') && (
                                        <>
                                          <Check className="w-3 h-3 text-green-600" />
                                          <span className="text-gray-600">{notification.data?.action_message || 'Accepted'}</span>
                                        </>
                                      )}
                                      {(notification.data?.action_type === 'reject' || notification.action_type === 'reject') && (
                                        <>
                                          <X className="w-3 h-3 text-red-600" />
                                          <span className="text-gray-600">{notification.data?.action_message || 'Rejected'}</span>
                                        </>
                                      )}
                                      {(notification.data?.action_type === 'counter' || notification.action_type === 'counter') && (
                                        <>
                                          <DollarSign className="w-3 h-3 text-blue-600" />
                                          <span className="text-gray-600">{notification.data?.action_message || 'Counter-offered'}</span>
                                        </>
                                      )}
                                      {!notification.data?.action_type && !notification.action_type && (
                                        <span className="text-gray-500">Action taken</span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                                      <ExternalLink className="w-3 h-3" />
                                      <span>Click to respond</span>
                                    </div>
                                  )
                                )}
                                
                                <div className="flex items-center space-x-1 mt-2 text-xs text-gray-400">
                                  <Clock className="w-3 h-3" />
                                  <span>
                                    {formatRelativeTime(notification.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                
                {notifications.length > 0 && (
                  <div className="p-3 border-t space-y-2">
                    <Link 
                      to="/notifications" 
                      className="block text-center text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                      onClick={() => setNotificationsOpen(false)}
                    >
                      View all notifications
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setNotificationsOpen(false)}
                      className="w-full"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Close
                    </Button>
                  </div>
                )}
                
                {notifications.length === 0 && (
                  <div className="p-3 border-t">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setNotificationsOpen(false)}
                      className="w-full"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Close
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* Mobile Hamburger Menu - Income, Investments, Budget */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden relative">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  More
                </div>
                {mobileMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <DropdownMenuItem key={item.name} asChild>
                      <Link 
                        to={item.href} 
                        className={`flex items-center space-x-3 ${active ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      >
                        <Icon className={`w-4 h-4 ${active ? 'text-emerald-600' : ''}`} />
                        <span>{item.name}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <span className="hidden md:inline font-medium">
                    {user?.full_name || user?.username}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.full_name || user?.username}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/biller-profiles" className="flex items-center space-x-2" data-testid="nav-biller-profiles">
                    <Building2 className="w-4 h-4" />
                    <span>Biller Profiles</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/help" className="flex items-center space-x-2">
                    <HelpCircle className="w-4 h-4" />
                    <span>Help & Guide</span>
                  </Link>
                </DropdownMenuItem>
                {/* Admin Panel - only visible for admin users */}
                {user?.email && ADMIN_EMAILS.includes(user.email) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center space-x-2 text-purple-600">
                        <Shield className="w-4 h-4" />
                        <span>Admin Panel</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout} 
                  className="flex items-center space-x-2 text-red-600 focus:text-red-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>
        </div>
      </div>

      {/* Notification Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNotification && (
                <>
                  {(() => {
                    const Icon = getNotificationIcon(selectedNotification.type);
                    return <Icon className={`w-5 h-5 ${getIconColor(selectedNotification.type)}`} />;
                  })()}
                  {selectedNotification.title}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedNotification && formatRelativeTime(selectedNotification.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedNotification && (
            <div className="space-y-4">
              {/* Notification Message */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">{selectedNotification.message}</p>
              </div>

              {/* Split Request Details */}
              {selectedNotification.type === 'expense_split_request' && selectedNotification.data && (
                <div className="space-y-4">
                  <Separator />
                  
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Split Details</h4>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-500 block">Amount</span>
                        <span className="font-semibold text-lg">
                          {selectedNotification.data.currency || 'USD'} {(selectedNotification.data.split_amount || selectedNotification.data.amount || 0).toFixed(2)}
                        </span>
                      </div>
                      
                      {selectedNotification.data.description && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-500 block">Description</span>
                          <span className="font-medium">{selectedNotification.data.description}</span>
                        </div>
                      )}
                    </div>

                    {selectedNotification.data.requester_name && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-500 block text-sm">Requested by</span>
                        <span className="font-medium">{selectedNotification.data.requester_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons for Split Requests */}
                  {!selectedNotification.action_taken && !selectedNotification.data?.action_taken && (
                    <div className="space-y-4 pt-2">
                      <Separator />
                      
                      {/* Counter Offer Input */}
                      <div className="space-y-2">
                        <Label htmlFor="counter-amount-navbar">Counter-Offer Amount (Optional)</Label>
                        <Input
                          id="counter-amount-navbar"
                          type="number"
                          placeholder={`Enter amount (max: ${(selectedNotification.data.split_amount || selectedNotification.data.amount || 0).toFixed(2)})`}
                          value={counterOfferAmount}
                          onChange={(e) => setCounterOfferAmount(e.target.value)}
                          step="0.01"
                        />
                        <p className="text-xs text-gray-500">
                          Leave empty and click Accept to pay the full amount, or enter a different amount and click Counter-Offer.
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSplitAction('accept')}
                          disabled={actionLoading}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          {actionLoading ? 'Processing...' : 'Accept'}
                        </Button>
                        
                        <Button
                          onClick={() => handleSplitAction('counter')}
                          disabled={actionLoading || !counterOfferAmount}
                          variant="outline"
                          className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Counter-Offer
                        </Button>
                        
                        <Button
                          onClick={() => handleSplitAction('reject')}
                          disabled={actionLoading}
                          variant="outline"
                          className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Show action taken message */}
                  {(selectedNotification.action_taken || selectedNotification.data?.action_taken) && (
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <div className="flex items-center gap-2">
                        {(selectedNotification.data?.action_type === 'accept') && <Check className="w-5 h-5 text-green-600" />}
                        {(selectedNotification.data?.action_type === 'reject') && <X className="w-5 h-5 text-red-600" />}
                        {(selectedNotification.data?.action_type === 'counter') && <DollarSign className="w-5 h-5 text-blue-600" />}
                        <span className="font-medium text-gray-700">
                          {selectedNotification.data?.action_message || 'Action already taken'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Budget Alert Details */}
              {selectedNotification.type === 'budget_exceeded' && selectedNotification.data && (
                <div className="space-y-3">
                  <Separator />
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-red-50 rounded-lg">
                      <span className="text-gray-500 block">Spent</span>
                      <span className="font-semibold text-red-600">${selectedNotification.data.spent?.toFixed(2)}</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-500 block">Budget Limit</span>
                      <span className="font-semibold">${selectedNotification.data.limit?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Settlement Initiated Details - Accept/Reject Offline Payment */}
              {selectedNotification.type === 'settlement_initiated' && selectedNotification.data && (
                <div className="space-y-4">
                  <Separator />
                  
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Payment Details</h4>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-500 block">Amount</span>
                        <span className="font-semibold text-lg">
                          {selectedNotification.data.currency || 'USD'} {(selectedNotification.data.amount || 0).toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-500 block">Payment Method</span>
                        <span className="font-medium capitalize">{selectedNotification.data.payment_method || 'N/A'}</span>
                      </div>
                    </div>

                    {selectedNotification.data.notes && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-500 block text-sm">Notes</span>
                        <span className="font-medium">{selectedNotification.data.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons for Settlement - Accept/Reject */}
                  {!selectedNotification.action_taken && selectedNotification.data?.requires_action && (
                    <div className="space-y-4 pt-2">
                      <Separator />
                      
                      <p className="text-sm text-gray-600">
                        Please confirm whether you received this offline payment.
                      </p>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleOfflineSettlement(selectedNotification.data.settlement_id, 'accept', selectedNotification.id)}
                          disabled={actionLoading}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          {actionLoading ? 'Processing...' : 'Accept Payment'}
                        </Button>
                        
                        <Button
                          onClick={() => handleOfflineSettlement(selectedNotification.data.settlement_id, 'reject', selectedNotification.id)}
                          disabled={actionLoading}
                          variant="outline"
                          className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject Payment
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Show action taken message */}
                  {selectedNotification.action_taken && (
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-gray-700">
                          Action already taken on this payment
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Other notification types - just show message */}
              {!['expense_split_request', 'budget_exceeded', 'settlement_initiated'].includes(selectedNotification.type) && (
                <div className="text-sm text-gray-500">
                  <p>Received on {formatDateDDMMYY(selectedNotification.created_at)}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
};

export default Navbar;